import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, BookOpen, Plane, TrendingUp, Users } from "lucide-react";
import { publicApi } from "../admin/api";
import SEO from "../components/SEO";
import NumberCounter from "../components/NumberCounter";
import MarketNeighborhoodsD3 from "../components/MarketNeighborhoodsD3";

const EASE = [0.22, 1, 0.36, 1];

export default function MercatoMilano() {
  const [data, setData] = useState(null);

  useEffect(() => {
    publicApi.get("/market-data").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) {
    return (
      <div className="min-h-[70svh] grid place-items-center text-white/40 pt-32">
        Caricamento…
      </div>
    );
  }

  const { kpis, categories, trend_revpar, drivers, sources } = data;
  const peakYear = trend_revpar.reduce(
    (acc, p) => (p.value > acc.value ? p : acc),
    trend_revpar[0] || { value: 1 },
  );
  const peakValue = peakYear?.value || 1;

  return (
    <div data-testid="mercato-milano-page">
      <SEO
        title={data.title}
        path="/mercato-milano"
        description="Mercato alberghiero milanese: ADR, occupancy, RevPAR, pipeline e investimenti. Una sintesi dei principali indicatori, ricostruita dai report pubblici degli operatori internazionali."
      />

      {/* === Hero typographic === */}
      <section className="relative min-h-[70svh] flex items-end overflow-hidden bg-[#08080A]">
        <div className="absolute inset-0 opacity-70">
          <div className="absolute -top-40 left-1/4 h-[600px] w-[600px] rounded-full bg-[#C48F56] blur-[200px] opacity-20" />
          <div className="absolute bottom-0 right-1/4 h-[500px] w-[500px] rounded-full bg-[#70142A] blur-[180px] opacity-15" />
        </div>
        <div
          className="absolute inset-0 opacity-[0.06] mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
          }}
        />
        <div className="absolute top-1/4 right-[-4%] font-display italic font-light text-white/[0.03] text-[28vw] leading-none pointer-events-none select-none">
          Milano
        </div>

        <div className="relative pt-40 pb-20 md:pb-28 px-6 md:px-12 max-w-[1440px] mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE }}
          >
            <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-white/70">
              <span className="h-px w-10 bg-[#C48F56]" />
              {data.eyebrow}
            </div>
            <h1
              data-testid="mercato-headline"
              className="mt-6 font-display text-balance tracking-tight text-white text-3xl sm:text-4xl md:text-5xl lg:text-5xl leading-[1.05] font-medium"
            >
              {data.headline.split("*").map((part, i) =>
                i % 2 === 1 ? (
                  <span key={i} className="text-[#C48F56]">{part}</span>
                ) : (
                  <span key={i}>{part}</span>
                ),
              )}
            </h1>
            <p className="mt-8 md:mt-10 max-w-2xl text-white/70 text-base md:text-lg leading-relaxed">
              {data.subhead}
            </p>
            <div className="mt-8 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
              <span className="h-px w-6 bg-white/20" />
              Ultimo aggiornamento · anno di riferimento {data.reference_year}
            </div>
          </motion.div>
        </div>
      </section>

      {/* === KPIs === */}
      <section
        data-testid="market-kpis"
        className="relative px-6 md:px-12 max-w-[1440px] mx-auto py-16 md:py-32"
      >
        <div className="grid lg:grid-cols-12 gap-10 mb-10 md:mb-14">
          <div className="lg:col-span-6">
            <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-white/50">
              <span className="h-px w-8 bg-[#C48F56]" />
              Indicatori chiave
            </div>
            <h2 className="mt-5 font-display text-4xl sm:text-5xl md:text-6xl tracking-tightest leading-[0.95]">
              I numeri di <span className="text-[#C48F56]">Milano.</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:pt-8">
            <p className="text-white/70 text-base md:text-lg leading-relaxed">
              Performance medie del mercato alberghiero milanese, tratte dagli
              ultimi market spotlight di STR/CoStar, HVS, Cushman & Wakefield e
              JLL. Ogni KPI riporta la fonte alla base.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {kpis.map((k, i) => (
            <motion.div
              key={k.label}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.05 }}
              transition={{ duration: 0.7, delay: i * 0.06, ease: EASE }}
              className="group relative rounded-2xl border border-white/10 bg-[#0F0F14] p-7 hover:border-[#C48F56]/40 transition-colors"
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/45">
                {k.label}
              </div>
              <div className="mt-4 flex items-baseline gap-1 font-display tracking-tight leading-none flex-wrap">
                <NumberCounter
                  value={k.value}
                  className="text-4xl sm:text-5xl md:text-6xl"
                />
                <span className="text-2xl sm:text-3xl md:text-4xl text-white/80">
                  {k.suffix}
                </span>
              </div>
              {(k.trend || k.trend_label) && (
                <div className="mt-5 flex items-center gap-2 text-xs">
                  {k.trend && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#C48F56]/15 text-[#C48F56] px-2.5 py-1 font-mono tracking-wider">
                      <TrendingUp size={11} /> {k.trend}
                    </span>
                  )}
                  <span className="text-white/45 text-[11px] font-mono uppercase tracking-[0.2em]">
                    {k.trend_label}
                  </span>
                </div>
              )}
              {k.note && (
                <div className="mt-5 pt-5 border-t border-white/5 text-[11px] text-white/40 leading-relaxed">
                  {k.note}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </section>

      {/* === RevPAR Trend === */}
      {trend_revpar.length > 0 && (
        <section
          data-testid="market-trend"
          className="relative border-t border-white/5 px-6 md:px-12 max-w-[1440px] mx-auto py-16 md:py-32"
        >
          <div className="grid lg:grid-cols-12 gap-10 mb-10 md:mb-14">
            <div className="lg:col-span-6">
              <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-white/50">
                <span className="h-px w-8 bg-[#C48F56]" />
                Recovery 2019 → 2024
              </div>
              <h2 className="mt-5 font-display text-4xl sm:text-5xl md:text-6xl tracking-tightest leading-[0.95]">
                Indice <span className="text-[#C48F56]">RevPAR.</span>
              </h2>
            </div>
            <div className="lg:col-span-6 lg:pt-8">
              <p className="text-white/70 text-base md:text-lg leading-relaxed">
                Indice RevPAR (base 2019 = 100) ricostruito da serie HVS / STR.
                Milano è uno dei mercati europei che hanno chiuso più
                rapidamente la fase post-pandemia, sorpassando i livelli reali
                pre-COVID già nel 2023.
              </p>
            </div>
          </div>

          <div className="rounded-3xl border border-white/10 bg-[#0F0F14] p-5 sm:p-8 md:p-12">
            <div className="flex items-end gap-2 sm:gap-4 md:gap-6 h-[240px] sm:h-[280px] md:h-[340px]">
              {trend_revpar.map((p, i) => {
                const heightPct = (p.value / peakValue) * 100;
                const isPeak = p.year === peakYear.year;
                return (
                  <div
                    key={p.year}
                    className="flex-1 h-full flex flex-col justify-end items-center gap-2 relative min-w-0"
                  >
                    <div className="font-mono text-[9px] sm:text-[10px] tabular-nums text-white/70">
                      {p.value}
                    </div>
                    <motion.div
                      initial={{ height: 0, opacity: 0.3 }}
                      whileInView={{
                        height: `${heightPct}%`,
                        opacity: 1,
                      }}
                      viewport={{ once: true, amount: 0.05 }}
                      transition={{
                        duration: 0.9,
                        delay: i * 0.08,
                        ease: EASE,
                      }}
                      className={`w-full rounded-t-md ${
                        isPeak
                          ? "bg-gradient-to-t from-[#C48F56] to-[#E5B782]"
                          : "bg-white/15 hover:bg-white/25 transition-colors"
                      }`}
                    />
                  </div>
                );
              })}
            </div>
            <div className="mt-4 flex gap-2 sm:gap-4 md:gap-6">
              {trend_revpar.map((p) => {
                const isPeak = p.year === peakYear.year;
                return (
                  <div
                    key={p.year}
                    className="flex-1 flex flex-col items-center gap-1 text-center min-w-0"
                  >
                    <div
                      className={`font-display text-lg sm:text-xl md:text-2xl ${
                        isPeak ? "text-[#C48F56]" : "text-white/80"
                      }`}
                    >
                      {p.year}
                    </div>
                    {p.label && (
                      <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/35 hidden md:block">
                        {p.label}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* === ADR by category === */}
      {categories.length > 0 && (
        <section
          data-testid="market-categories"
          className="relative border-t border-white/5 px-6 md:px-12 max-w-[1440px] mx-auto py-16 md:py-32"
        >
          <div className="grid lg:grid-cols-12 gap-10 mb-10 md:mb-14">
            <div className="lg:col-span-6">
              <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-white/50">
                <span className="h-px w-8 bg-[#C48F56]" />
                ADR per categoria
              </div>
              <h2 className="mt-5 font-display text-4xl sm:text-5xl md:text-6xl tracking-tightest leading-[0.95]">
                Tariffe medie <span className="text-[#C48F56]">2024.</span>
              </h2>
            </div>
            <div className="lg:col-span-6 lg:pt-8">
              <p className="text-white/70 text-base md:text-lg leading-relaxed">
                Range di Average Daily Rate per fascia di stelle a Milano.
                Il segmento upscale (4★) — in cui opera il portafoglio Invest
                Hospitality — è quello con la migliore combinazione di
                occupancy stabile e tariffe in crescita.
              </p>
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-[#0F0F14] overflow-hidden">
            {/* Desktop header (hidden mobile) */}
            <div className="hidden md:grid md:grid-cols-12 px-8 py-4 border-b border-white/5 font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
              <div className="md:col-span-4">Categoria</div>
              <div className="md:col-span-2 text-right">ADR</div>
              <div className="md:col-span-2 text-right">Occupancy</div>
              <div className="md:col-span-4 text-right">Note</div>
            </div>
            {categories.map((c, i) => (
              <motion.div
                key={c.category}
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.05 }}
                transition={{ duration: 0.6, delay: i * 0.08, ease: EASE }}
                className="border-b border-white/5 last:border-b-0 hover:bg-white/[0.02] transition-colors"
              >
                {/* Mobile: stacked card */}
                <div className="md:hidden p-5 space-y-3">
                  <div className="font-display text-2xl">{c.category}</div>
                  <div className="grid grid-cols-2 gap-3 pt-2 border-t border-white/5">
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                        ADR
                      </div>
                      <div className="mt-1.5 font-display text-2xl text-[#C48F56]">
                        {c.adr}
                      </div>
                    </div>
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                        Occupancy
                      </div>
                      <div className="mt-1.5 font-mono text-base text-white/70 tabular-nums">
                        {c.occupancy}
                      </div>
                    </div>
                  </div>
                  {c.note && (
                    <div className="text-xs text-white/45 leading-relaxed pt-2">
                      {c.note}
                    </div>
                  )}
                </div>
                {/* Desktop: row */}
                <div className="hidden md:grid md:grid-cols-12 px-8 py-8 items-center">
                  <div className="md:col-span-4 font-display text-2xl">
                    {c.category}
                  </div>
                  <div className="md:col-span-2 text-right font-display text-3xl text-[#C48F56]">
                    {c.adr}
                  </div>
                  <div className="md:col-span-2 text-right font-mono text-sm text-white/70 tabular-nums">
                    {c.occupancy}
                  </div>
                  <div className="md:col-span-4 text-right text-xs text-white/45 leading-relaxed">
                    {c.note}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* === Milan neighborhood-level micro-data (D3) === */}
      <MarketNeighborhoodsD3 />

      {/* === Pipeline citywide vs Invest Hospitality === */}
      <section
        data-testid="market-pipeline"
        className="relative border-t border-white/5 px-6 md:px-12 max-w-[1440px] mx-auto py-16 md:py-32"
      >
        <div className="grid lg:grid-cols-12 gap-10 mb-10 md:mb-14">
          <div className="lg:col-span-6">
            <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-white/50">
              <span className="h-px w-8 bg-[#C48F56]" />
              Pipeline 2025 → 2027
            </div>
            <h2 className="mt-5 font-display text-4xl sm:text-5xl md:text-6xl tracking-tightest leading-[0.95]">
              Il nostro <span className="text-[#C48F56]">peso.</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:pt-8">
            <p className="text-white/70 text-base md:text-lg leading-relaxed">
              Confronto tra le camere in pipeline a livello cittadino (HVS) e
              il contributo diretto di Invest Hospitality. Cinque cantieri
              aperti tra il 2026 e il 2027 ci collocano tra i player più
              attivi sul mercato milanese.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <PipelineCard
            label="Mercato Milano"
            rooms={data.pipeline_citywide_rooms}
            description={data.pipeline_citywide_label}
            tone="muted"
          />
          <PipelineCard
            label="Invest Hospitality"
            rooms={data.pipeline_our_rooms}
            description={data.pipeline_our_label}
            tone="brass"
          />
        </div>
      </section>

      {/* === Drivers === */}
      {drivers.length > 0 && (
        <section
          data-testid="market-drivers"
          className="relative border-t border-white/5 px-6 md:px-12 max-w-[1440px] mx-auto py-16 md:py-32"
        >
          <div className="grid lg:grid-cols-12 gap-10 mb-10 md:mb-14">
            <div className="lg:col-span-6">
              <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-white/50">
                <span className="h-px w-8 bg-[#C48F56]" />
                Driver di domanda
              </div>
              <h2 className="mt-5 font-display text-4xl sm:text-5xl md:text-6xl tracking-tightest leading-[0.95]">
                Cosa <span className="text-[#C48F56]">muove</span> Milano.
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {drivers.map((d, i) => {
              const Icon = pickDriverIcon(d.label);
              return (
                <motion.div
                  key={d.label}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.05 }}
                  transition={{
                    duration: 0.6,
                    delay: i * 0.05,
                    ease: EASE,
                  }}
                  className="rounded-2xl border border-white/10 bg-[#0F0F14] p-5 md:p-6 hover:border-white/20 transition-colors"
                >
                  <div className="flex items-center gap-3 text-[#C48F56]">
                    <Icon size={14} />
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em]">
                      {d.label}
                    </span>
                  </div>
                  <div className="mt-4 font-display text-3xl md:text-4xl tracking-tight leading-none">
                    {d.value}
                  </div>
                  {d.source && (
                    <div className="mt-3 text-[11px] text-white/40">
                      {d.source}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </section>
      )}

      {/* === Sources === */}
      {sources.length > 0 && (
        <section
          data-testid="market-sources"
          className="relative border-t border-white/5 px-6 md:px-12 max-w-[1440px] mx-auto py-20"
        >
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-white/50">
            <BookOpen size={12} className="text-[#C48F56]" />
            Fonti consultate
          </div>
          <div className="mt-6 flex flex-wrap gap-x-3 gap-y-2 text-sm text-white/55">
            {sources.map((s, i) => (
              <span key={s.name} className="inline-flex items-center gap-2">
                {s.url ? (
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    className="hover:text-white underline-offset-4 hover:underline"
                  >
                    {s.name}
                  </a>
                ) : (
                  <span>{s.name}</span>
                )}
                {s.year && (
                  <span className="font-mono text-[10px] text-white/30">
                    · {s.year}
                  </span>
                )}
                {i < sources.length - 1 && (
                  <span className="text-white/15">·</span>
                )}
              </span>
            ))}
          </div>
          <div className="mt-6 text-xs text-white/35 leading-relaxed max-w-3xl">
            I dati sono ricostruiti a partire dai market spotlight pubblici dei
            principali advisor internazionali. Per analisi più granulari
            (zona, segmento, comp set) Invest Hospitality si avvale di
            sottoscrizioni dedicate STR/CoStar e HotStats.
          </div>
        </section>
      )}

      {/* === CTA === */}
      <section className="relative border-t border-white/5 px-6 md:px-12 max-w-[1440px] mx-auto py-16 md:py-28">
        <div className="grid md:grid-cols-2 gap-8 md:gap-10 md:items-end">
          <div>
            <h3 className="font-display text-3xl sm:text-4xl md:text-5xl tracking-tightest leading-[1.05] md:leading-[1] text-balance">
              Ti interessa <span className="text-[#C48F56]">approfondire?</span>
            </h3>
            <p className="mt-4 text-white/70 max-w-md text-base leading-relaxed">
              La nostra area investitori contiene KPI di portafoglio,
              milestones di pipeline e documenti riservati.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 md:justify-end">
            <Link
              to="/investor"
              data-testid="mercato-cta-investor"
              className="inline-flex items-center gap-2 rounded-full bg-white text-black px-6 py-3 text-sm font-medium hover:bg-[#C48F56] transition-colors"
            >
              Area investitori <ArrowUpRight size={14} />
            </Link>
            <Link
              to="/contatti"
              data-testid="mercato-cta-contact"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm hover:bg-white/5 transition-colors"
            >
              Parliamone
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function PipelineCard({ label, rooms, description, tone }) {
  const isBrass = tone === "brass";
  return (
    <div
      className={`relative rounded-2xl p-6 sm:p-8 md:p-10 overflow-hidden ${
        isBrass
          ? "bg-gradient-to-br from-[#1a1410] to-[#0F0F14] border border-[#C48F56]/30"
          : "border border-white/10 bg-[#0F0F14]"
      }`}
    >
      {isBrass && (
        <div className="absolute -top-20 -right-20 h-[300px] w-[300px] rounded-full bg-[#C48F56] blur-[100px] opacity-15 pointer-events-none" />
      )}
      <div className="relative">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/45">
          {label}
        </div>
        <div className="mt-5 font-display tracking-tight leading-none">
          <NumberCounter
            value={rooms}
            className={`block text-6xl sm:text-7xl md:text-8xl ${isBrass ? "text-[#C48F56]" : ""}`}
          />
          <span className="mt-2 inline-block text-lg sm:text-xl md:text-2xl text-white/70 font-display">
            camere
          </span>
        </div>
        <div className="mt-5 md:mt-6 text-white/70 text-sm leading-relaxed max-w-md">
          {description}
        </div>
      </div>
    </div>
  );
}

function pickDriverIcon(label) {
  const l = label.toLowerCase();
  if (l.includes("passegg") || l.includes("malpensa") || l.includes("aero")) {
    return Plane;
  }
  if (l.includes("visit") || l.includes("pernot")) return Users;
  if (l.includes("pipeline") || l.includes("hotel")) return TrendingUp;
  return TrendingUp;
}
